<template>
  <div v-if="livingOption && subsection" class="subsection">    
    <main-content
      :background-image="subsection.backgroundImage"
      right-sidebar="true"
    >
      <!-- issuu modal -->
      <transition name="fast-fade">
        <div v-if="isIssuuModalActive" class="issuu" @click.stop="isIssuuModalActive = false">
          <div class="issuu__inner">
            <div class="issuu__control-overlay"></div>
            <div style="position:relative;padding-top:max(40%,326px);height:0;width:100%;" @click.stop="">
              <iframe sandbox="allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-scripts allow-same-origin allow-popups allow-modals allow-popups-to-escape-sandbox" allowfullscreen="true" style="position:absolute;border:none;width:100%;height:100%;left:0;right:0;top:0;bottom:0;" :src="subsection.issuuEmbedUrl"></iframe>
            </div>
          </div>
        </div>
      </transition>

      <image-gallery 
        :key="subsection._id"
        :autoplay="false" 
        :contain-images="false"
        :images="subsection.carouselImages" 
        :show-nav="true"
      />

      <div v-if="subsectionChildType === 'property'">
        <div
          class="property-indicator"
          :class="{ 'property-indicator--alt-pos': useAlternateIndicatorPosition }"
        >
          <div>{{ countAvailable }} - Available</div>
          <div>{{ countOnHold }} - Reserved</div>
          <div>{{ countSold }} - Sold</div>
          <div>{{ countBlocked }} - Blocked</div>
          <availability-marker status="Available" x="-55" y="7"></availability-marker>
          <availability-marker status="On Hold" x="-55" y="64"></availability-marker>
          <availability-marker status="Sold" x="-55" y="120"></availability-marker>
          <availability-marker status="Management Blocked" x="-55" y="176"></availability-marker>
        </div>
        <router-link
          v-for="child in children"
          class="touch"
          :key="child._id"
          tag="div"
          :to="getRouterLink(child)"
        >
          <availability-marker 
            :filtered="child.filter"
            :x="child.marker.x"
            :y="child.marker.y"
            :status="child.status"
          ></availability-marker>
        </router-link>
      </div>
      <transition name="fade">
        <img v-if="showAllProperties" class="main-content__background" :key="subsection.allPropertiesFloorplate" :src="`${$store.state.assetsPath}${subsection.allPropertiesFloorplate}`">
      </transition>
      <transition name="fade">
        <div v-if="showAllProperties">
          <router-link
            v-for="property in allProperties"
            class="touch"
            :key="property._id"
            tag="div"
            :to="property.routerLink"
          >
            <availability-marker 
              :filtered="property.filter"
              :x="property.livingOptionMarker.x"
              :y="property.livingOptionMarker.y"
              :status="property.status"
            ></availability-marker>
          </router-link>
        </div>      
      </transition>
      <transition name="fade">
        <action-bar v-if="!showAllProperties">{{ actionText }}</action-bar>
      </transition>      
    </main-content>
    <right-sidebar
      :logo="$store.state.config.sidebarLogo"
      :minimap="livingOption.sidebarMinimap"
      :heading="sidebarHeading"
      :description="sidebarDescription"
      :navigation-items="navigationItems"
      :enable-slide-out="!subsection.hideSidebarFilter"
    >
      <!-- <div slot="top-slot">
        <property-filter/>
      </div> -->
      <div v-if="subsection.minimapImage" class="description-extra" slot="description-extra">
        <img :src="$store.state.assetsPath + subsection.minimapImage" alt="">
      </div>
      <div v-if="sidebarProperty" class="description" slot="top-slot" style="margin: 0 0 7.5rem;">
        <div class="property__label">
          Property
        </div>
        <div class="property__size">
          {{ sidebarProperty.label }}
        </div>

        <div class="property__size-group">
          <div class="property__size-group-wrapper">
            <div class="property__label">
              Plot Size
            </div>
            <div class="property__size">
              {{ sidebarProperty.plotMsq }}<span>m<sup>2</sup></span>
            </div>
          </div>
          <div class="property__size-group-wrapper">
            <div class="property__label">
              View
            </div>
            <div class="property__size">
              {{ sidebarProperty.view }}
            </div>
          </div>
        </div>

        <div class="property__favourites" style="margin: 2rem 0 0;">
          <transition name="fast-fade">
            <div v-if="isFavouriteLimitShowing" class="favourites-warning">You've reached your favourites limit</div>
          </transition>
          <button v-if="!isFavourite" class="btn btn--gold" @click="favourites.length < 12 ? addToFavourites(sidebarProperty._id) : displayFavouritesLimitWarning()">Add to favourites</button>
          <button v-if="isFavourite" class="btn btn--red" @click="removeFromFavourites(sidebarProperty._id)">Remove from favourites</button>
          <router-link 
            class="btn btn--blue"
            tag="button"
            to="/live/favourites"
          >View favourites</router-link>
        </div>


      </div>
      <template slot="bottom-slot">
        <button v-if="subsection.issuuEmbedUrl" class="btn btn--blue touch" @click="openIssuuModal">
          <template v-if="language.code === 'en'">
            {{ isIssuuModalActive ? 'Hide Brochure' : 'View Brochure' }}
          </template>
          <template v-if="language.code === 'ar'">
            {{ isIssuuModalActive ? 'إخفاء الكتيب' : 'عرض الكتيّب' }}
          </template>
        </button>
        <button
          v-if="subsection.allPropertiesFloorplate && subsection.allProperties.length"
          class="btn btn--blue touch"
          @click="toggleShowAllProperties()"
        >{{ showAllProperties ? 'Hide All Properties' : 'View All Properties' }}</button>
        <router-link tag="button" class="right-sidebar__back" :to="backRoute"><fa icon="chevron-left"/>{{ language.code === 'en' ? 'Back' : 'عودة' }}</router-link>
      </template>
      <template slot="slide-out-slot">
        <property-filter/>
      </template>
    </right-sidebar>
  </div>  
</template>
<script>
// A subsection either links to properties or other subsections
import ActionBar from '@/components/ActionBar'
import AvailabilityMarker from '@/components/AvailabilityMarker'
import ImageGallery from '@/components/ImageGallery'
import MainContent from '@/components/MainContent'
import PropertyFilter from '@/components/PropertyFilter'
import RightSidebar from '@/components/RightSidebar'
import { mapGetters } from 'vuex'
import _ from 'lodash'
export default {
  name: 'subsection',
  components: { ActionBar, AvailabilityMarker, ImageGallery, MainContent, PropertyFilter, RightSidebar },

  data () {
    return {
      isIssuuModalActive: false,
      showAllProperties: false,
      favouritesTimeout: null,
      isFavouriteLimitShowing: false
    }
  },

  computed: {
    ...mapGetters({
      language: 'language',
      livingOption: 'property/activeLivingOption',
      subsection: 'property/activeSubsection',
      subSubsections: 'property/subSubsectionsWithFiltering',
      subsections: 'property/subsections',
      propertiesFiltered: 'property/propertiesFiltered',
      favourites: 'favourites/favourites',
    }),

    countAvailable () {
      return this.children.filter(property => property.status === 'Available').length
    },

    countOnHold () {
      return this.children.filter(property => property.status === 'On Hold').length
    },

    countBlocked () {
      return this.children.filter(property => property.status === 'Management Blocked').length
    },

    countSold () {
      return this.children.filter(property => property.status === 'Sold').length
    },

    allProperties () {
      let properties = []
      this.subsection.allProperties.forEach(property => {
        const routerLink = this.generateRouterLink(property._id)
        if (routerLink && property.livingOptionMarker.x !== 100) {
          property.routerLink = routerLink
          property.filter = false
          property.models.forEach(model => {
            const beds = parseInt(model.beds) > 4 ? 4 : parseInt(model.beds)
            if (this.$store.state.property.inputType && this.$store.state.property.inputType !== model.type) {
              property.filter =  true
            } else if ((this.$store.state.property.inputBeds || this.$store.state.property.inputBeds === 0) && this.$store.state.property.inputBeds !== beds) {
              property.filter =  true
            }
          })

          properties.push(property)
        }
      })
      return properties
    },    

    actionText () {  
      let propertyType = this.subsectionChildType === 'property' && this.livingOption.propertyTypes.length ? this.livingOption.propertyTypes[0] : this.subsection.subsectionType
      if (!propertyType && this.subsection.properties.length) {
        propertyType = this.subsection.properties[0].models[0].type
      } else {
        return ''
      }
      const grammerArticle = ['a', 'e', 'i', 'o', 'u'].includes(propertyType.charAt(0)) ? 'an' : 'a' // a/an
      return `select ${grammerArticle} ${propertyType}` // select an/a apartment/townhouse
    },

    backRoute () {
      if (this.$store.state.route.query.parentSubsectionId) {
        return`/live/${this.livingOption._id}/subsection/${this.$store.state.route.query.parentSubsectionId}`
      } else {

        if (this.livingOption.subsections.length === 1) { // if only one subsection, skip it and go straight to parent page
          return`/live`
        } else {
          return`/live/${this.livingOption._id}`
        }
      }
    },

    children () {
      if (this.subsectionChildType === 'property') {
        return this.propertiesFiltered
      } else {
        return this.subSubsections
        // return this.subsection.subsections
      }
    },

    navigationItems () {
      // if subsection has subsection children then create nav items from those children instead of living options subsections
      if (this.$store.state.route.query.parentSubsectionId && this.subsection && !this.subsection.subsections) {
        
        // subsection inception end
        const subsection = _.find(this.subsections, subsection => {
          return subsection._id === this.$store.state.route.query.parentSubsectionId
        })

        return subsection.subsections.map(subsection => {
          return {
            label: subsection.displayName,
            to: `/live/${this.livingOption._id}/subsection/${subsection._id}?parentSubsectionId=${this.$store.state.route.query.parentSubsectionId}`,
            disabled: subsection.ignoreSidebarFiltering ? false : subsection.filter
          }
        })
      } else if (this.$store.state.route.query.parentSubsectionId && this.subsection && this.subsection.subsections) {
        // subsection inception
        return this.subSubsections.map(subsection => {
          return {
            label: subsection.displayName,
            to: `/live/${this.livingOption._id}/subsection/${subsection._id}?parentSubsectionId=${this.subsection._id}`,
            disabled: subsection.ignoreSidebarFiltering ? false : subsection.filter
          }
        })
      } else if (this.subsection.subsections && this.subsection.subsections.length) {
        // const filteredSubsections = this.subsection.subsections
        const filteredSubsections = this.subSubsections
        return filteredSubsections.map(subsection => {
          return {
            label: subsection.displayName,
            to: `/live/${this.livingOption._id}/subsection/${subsection._id}?parentSubsectionId=${this.subsection._id}`,
            disabled: subsection.ignoreSidebarFiltering ? false : subsection.filter
          }
        })
      } else {
        const filteredSubsections = this.livingOption.subsections
        return filteredSubsections.map(subsection => {
          return {
            label: subsection.displayName,
            to: `/live/${this.livingOption._id}/subsection/${subsection._id}`,
            disabled: subsection.ignoreSidebarFiltering ? false : subsection.filter
          }
        })
      }
    },

    sidebarDescription () {
      if (this.$store.state.route.query.parentSubsectionId && this.subsection.subsections && this.subsection.subsections.length) {
        const parentSubsection = _.find(this.livingOption.subsections, subsection => {
          return subsection._id === this.$store.state.route.query.parentSubsectionId
        })
        return parentSubsection.description ? parentSubsection.description : this.livingOption.description
      } else {
        return this.subsection.description ? this.subsection.description : this.livingOption.description
      }
    },

    sidebarHeading () {
      if (this.$store.state.route.query.parentSubsectionId && this.subsection.subsections && this.subsection.subsections.length) {
        // get heading from subsection first, then parent if blank
        if (this.subsection.displayName[this.language.code]) {
          return `${this.livingOption.label[this.language.code]} - ${this.subsection.displayName[this.language.code]}`
        }

        const parentSubsection = _.find(this.livingOption.subsections, subsection => {
          return subsection._id === this.$store.state.route.query.parentSubsectionId
        })
        return `${this.livingOption.label[this.language.code]} - ${parentSubsection.displayName[this.language.code]}`
      } else if (this.subsectionChildType === 'subsection') {
        return `${this.livingOption.label[this.language.code]} - ${this.subsection.displayName[this.language.code]}`
      } else {
        return this.livingOption.label[this.language.code]
      }
    },

    subsectionChildType () {
      if (this.subsection.subsections && this.subsection.subsections.length) {
        return 'subsection'
      } else {
        return 'property'
      }
    },

    sidebarProperty () {
      return this.propertiesFiltered.find(property => property._id === this.$route.query.property)
    },

    isFavourite () {
      return _.find(this.favourites, favourite => { 
        return favourite.propertyId === this.sidebarProperty._id 
      })
    },

    useAlternateIndicatorPosition () {
      return this.subsection._id === '60519bb09106ce12d5a63887'
    }
  },

  watch: {
    $route () {
      if (!this.subsection.allPropertiesFloorplate || !this.subsection.allProperties.length) {
        this.showAllProperties = false
      }

      this.isIssuuModalActive = false
    }
  },

  mounted () {
    this.showAllProperties = this.$route.query.showAllProperties
  },  

  methods: {
    generateRouterLink (id) {
      let routerLink = false
      this.subsection.subsections.forEach(subsection => {
        if (subsection.propertyIds.includes(id)) {
          routerLink = `/live/${this.livingOption._id}/subsection/${subsection._id}/property/${id}`
        }
      })
      return routerLink
    },

    toggleShowAllProperties () {
      this.showAllProperties = !this.showAllProperties
      this.$router.push({ query: { showAllProperties: this.showAllProperties }})
    },

    getRouterLink (child) {
      if (this.subsection.disablePropertySelection) {
        return this.$route.fullPath
      }

      if (this.subsection._id === '63d09fb1f896654418ce6ec8') {
        return `/live/${this.livingOption._id}/subsection/${this.subsection._id}?property=${child._id}`
      }
      
      if (this.subsectionChildType === 'property') {
        return `/live/${this.livingOption._id}/subsection/${this.subsection._id}/property/${child._id}`
      } else {
        return `/live/${this.livingOption._id}/subsection/${child._id}`
      }
    },

    openIssuuModal () {
      this.$store.commit('sidebar/CLOSE_SLIDEOUT')
      this.isIssuuModalActive = !this.isIssuuModalActive
    },

    displayFavouritesLimitWarning () {
      if (this.favouritesTimeout) {
        clearTimeout(this.favouritesTimeout)
      }

      this.isFavouriteLimitShowing = true
      this.favouritesTimeout = setTimeout(() => {
        this.isFavouriteLimitShowing = false
      }, 3000);
    },

    addToFavourites (propertyId) {
      const data = {
        propertyId: propertyId,
        subsectionId: this.subsection._id,
        livingId: this.livingOption._id
      }
      this.$store.commit('favourites/ADD_TO_FAVOURITES', data)
    },

    removeFromFavourites (propertyId) {
      this.$store.commit('favourites/REMOVE_FROM_FAVOURITES', propertyId)
    }
  }
}
</script>
<style lang="scss">
@import "@/styles/_mixins.scss";
@import "@/styles/_variables.scss";

.property-indicator {
  pointer-events: none;
  position: absolute;
  bottom: 2rem;
  left: 2rem;
  background-color: rgba($dblue, 0.85);
  height: 14rem;
  padding: 1rem;
  font-size: 1.4rem;
  width: 18rem;

  &--alt-pos {
    bottom: auto;
    top: 2rem;
  }

  .availability-marker {
    transform: scale(0.7);
  }

  > div {
    line-height: 2;
    font-weight: 200;
    margin-left: 2.5rem;
  }
}

.subsection {
  &__logo {
    position: absolute;
    top: 3rem;
    right: 4rem;
    z-index: 999;
    max-height: 10rem;
  }
}

.description {
  display: flex;
  flex-direction: column;
}

.description-extra {
  width: 30rem;

  img {
    width: 100%;
  }
}

.property {
  &__subsection {
    font-size: 1.9rem;
    text-transform: uppercase;
    margin-top: -1.5rem;
    opacity: 0.7;
  }

  &__label {
    // color: $lblue;
    font-size: 1.5rem;
    text-transform: uppercase;
    margin-top: 2.5rem;
    opacity: 0.7;
  }

  &__icon {
    font-size: 3rem;
    margin-top: 1rem;
    span {
      font-size: 2.7rem;
      margin-right: 1rem;
    }
  }

  &__size-group {
    display: flex;
    width: 100%;
  }

  &__size-group-wrapper {
    margin-right: 2rem;
    width: 50%;
  }

  &__price, &__size {
    font-size: 2.2rem;

    span {
      font-size: 2rem;
      // opacity: 0.8;
      margin-left: 0.3rem;
    }

    .small {
      font-size: 1.4rem;
    }
  }

  &__favourites {
    margin: auto 0 0;
    position: relative;
    width: 26rem;

    .btn {
      color: $black;
      font-family: $touchscreen-font;
      font-size: 1.4rem;
      margin: 1rem 0 0;
      padding: 1rem 0 .8rem;
      text-transform: uppercase;
      width: 26rem;

      .ar & {
        font-family: 'GE Dinkum';
      }        

      &--gold {
        @include btn($gold, $gold);
      }

      &--red {
        @include btn($red, $red);
        color: $white;
      }

      &--blue {
        @include btn($ddblue, $ddblue);
        color: $white;
      }
    }
  }
}
</style>